import React, { useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { ArrowLeft, Save } from "lucide-react";
import {
  createState,
  updateState,
  fetchStateById,
  resetStateStatuses,
} from "../../store/slices/statesSlice";
import { TextField, CheckboxField } from "../../components/ui/FormControls";
import { showApiError, showApiSuccess } from "../../utils/toast";
import Loader from "../../components/Loader";

const stateSchema = yup.object({
  name: yup
    .string()
    .required("State name is required")
    .max(255, "State name must be less than 255 characters"),
  status: yup.boolean(),
});

export default function AddStatePage() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const editUuid = searchParams.get("edit");

  const {
    state,
    stateCreateStatus,
    stateUpdateStatus,
    stateFetchStatus,
    loading,
  } = useSelector((state) => state.states);

  const {
    register,
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(stateSchema),
    defaultValues: {
      name: "",
      status: true,
    },
  });

  useEffect(() => {
    if (editUuid) {
      dispatch(fetchStateById(editUuid));
    }
  }, [editUuid, dispatch]);

  useEffect(() => {
    if (editUuid && state && stateFetchStatus === "success") {
      reset({
        name: state.name || "",
        status: Boolean(state.status),
      });
    }
  }, [state, stateFetchStatus, editUuid, reset]);

  const onSubmit = async (formData) => {
    try {
      const payload = {
        name: formData.name,
        status: formData.status,
      };

      if (editUuid) {
        await dispatch(updateState({ uuid: editUuid, data: payload })).unwrap();
        showApiSuccess("State updated successfully");
      } else {
        await dispatch(createState(payload)).unwrap();
        showApiSuccess("State created successfully");
      }

      navigate("/states");
    } catch (error) {
      showApiError(null, error || `Failed to ${editUuid ? "update" : "create"} state`);
    }
  };

  useEffect(() => {
    return () => {
      dispatch(resetStateStatuses());
    };
  }, [dispatch]);

  const isSubmitting = stateCreateStatus === "loading" || stateUpdateStatus === "loading";

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {loading && <Loader />}

      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate("/states")}
              className="text-gray-600 hover:text-gray-900"
            >
              <ArrowLeft className="h-6 w-6" />
            </button>
            <div>
              <h2 className="text-2xl font-bold text-gray-900">
                {editUuid ? "Edit State" : "Add New State"}
              </h2>
              <p className="text-sm text-gray-500 mt-1">
                {editUuid ? "Update state information" : "Create a new state"}
              </p>
            </div>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            State Information
          </h3>

          <div className="space-y-4">
            <div>
              <TextField
                label="State Name"
                required
                placeholder="Enter state name"
                {...register("name")}
                error={errors?.name?.message}
              />
            </div>

            <div>
              <Controller
                control={control}
                name="status"
                render={({ field }) => (
                  <CheckboxField
                    label="Active Status"
                    checked={Boolean(field.value)}
                    onChange={(checked) => field.onChange(checked)}
                  />
                )}
              />
              <p className="text-sm text-gray-500 mt-1">
                Enable this state to make it available for selection
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex justify-end space-x-4">
            <button
              type="button"
              onClick={() => navigate("/states")}
              className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Save className="h-4 w-4" />
              <span>
                {isSubmitting ? "Saving..." : editUuid ? "Update State" : "Create State"}
              </span>
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}
